/**
 * @genesis_domain Settings — Preferences Query Hook
 * @genesis_version V9.1
 * @genesis_intent TanStack Query hook for fetching the authenticated user's
 *   preferences profile. Implements token-aware async guard to prevent
 *   premature 401 requests during React 19 aggressive mounting.
 * @genesis_boundaries FORBIDDEN from containing mutation logic, UI rendering,
 *   or raw fetch() calls. Pure query orchestration only.
 * @genesis_ai_trail Phase 3 Hybrid Sync Preferences. Follows use[Entity]Query
 *   naming registry. Uses preferenceKeys for centralized cache key management.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/context/AuthContext';
import { preferenceKeys } from '@/core/api/queryKeys';
import { fetchPreferences } from '@/domains/settings/services/preferencesApiService';

/**
 * Fetch the authenticated user's preferences profile.
 *
 * Token Guard: The query is disabled until the Supabase session access_token is
 * available. This prevents React 19's aggressive component mounting from firing
 * 401 requests before session hydration completes.
 */
export function usePreferencesQuery() {
    const { session } = useAuth();

    return useQuery({
        queryKey: preferenceKeys.profile(),
        queryFn: ({ signal }) => fetchPreferences(signal),
        enabled: !!session?.access_token,
        staleTime: 5 * 60 * 1000,
    });
}
